import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CheckRegistration from "./CheckRegistration";

import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import BGLogo from "../assets/images/123votelogo.png";

type PartnerStatusResponse = {
  success: boolean;
  message: string;
  data: {
    active: boolean;
    plan: string;
    whiteLabelEnabled: boolean;
    branding: {
      primaryColor: string;
      logoUrl: string | null;
    };
    features: {
      smsEnabled: boolean;
      automationEnabled: boolean;
      exportsEnabled: boolean;
    };
  };
};

const apiUrl = process.env.REACT_APP_API_URL || "";

export default function EmbedStart() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<PartnerStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const embedId = searchParams.get("embed_id") || "";
  const embed = searchParams.get("embed") === "true";

  const hostOrigin = useMemo(() => {
    try {
      if (!document.referrer) return "";
      return new URL(document.referrer).origin;
    } catch {
      return "";
    }
  }, []);


  useEffect(() => {
    if (!embed && !embedId) {
      setLoading(false);
      setError("Invalid embed request.");
      return;
    }

    if (!embedId) {
      setLoading(false);
      setError("Missing embed ID.");
      return;
    }

    window.sessionStorage.setItem("embedded_partner_id", embedId);

    const controller = new AbortController();

    const fetchPartnerStatus = async () => {
      setLoading(true);
      setError(null);

      try {


        const res = await fetch(`${apiUrl}/partner-status`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-embed-id": embedId,
            // "Referer": "https://reeboot.vote",
          },

        });

        if (!res.ok) {
          throw new Error("Partner status lookup failed.");
        }

        const payload = (await res.json()) as PartnerStatusResponse;
        setStatus(payload);

        if (!payload.data.active) {
          setError("This embed feature has been disabled for your organisation.");
        }
      } catch (err) {
        if (err instanceof Error && err.message === "Partner status lookup failed") {
          setError("This embed feature has been disabled for your organisation.");
        }
        setError("This  partner’s civic tool has been disabled for your organisation.");
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerStatus();

    return () => controller.abort();
  }, [embed, embedId, hostOrigin]);



  const homeActions = [
    {
      title: "Check your Registration Status",
      subtitle: "Not sure if you’re registered? Let’s check!",
    },
    {
      title: "Register to Vote",
      subtitle: "...In Less than 2 minutes!",
    },
    {
      title: "Check My Polling Location",
      subtitle: "Know where to go?",
    },
    {
      title: "Vote by Mail",
      subtitle: "Request to vote by mail on state site",
    },
    {
      title: "Get Election Reminders",
      subtitle: "Stay notified about election information",
    },
    // {
    //     title: "Get Involved!",
    //     subtitle: "Help us expand Democracy!",
    // },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
        <p className="text-sm text-slate-700">Loading voter widget...</p>
      </div>
    );
  }

  if (error || !status?.data?.active) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-slate-50">
        
        <p className="text-sm text-slate-700">{error || "This partner’s civic tool is currently disabled."}</p>
        
        <p>kindly reach out to <a className="text-underline text-blue-500" href="mailto:support@123-vote.com" target="_blank">123 Vote Support</a> for enquiries and further assistance</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 text-white min-h-screen">
      <main
        className="relative min-h-screen flex flex-col items-center justify-start pt-10"
        style={{ position: "relative" }}
      >
        {/* Faint background logo with overlay */}
        {status?.data?.plan === "free" && <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              width: "100vw",
              height: "100vh",
              backgroundImage: `url(${BGLogo})`,
              backgroundRepeat: "repeat",
              backgroundSize: "240px",
              opacity: 0.03, // Adjust for faintness
              filter: "blur(0.5px) grayscale(1)",
            }}
          />
        </div>
        }
        {/* Content */}
        <div className="relative z-10 w-full flex flex-col items-center">
          <h1 className="text-white text-center text-2xl max-w-3xl p-5 md:text-2xl font-bold mt-8 mb-14 drop-shadow-lg">
            Scaling Democracy. Expanding the Electorate. Empowering Communities.
          </h1>
          <div className="w-full flex flex-col items-center space-y-8 max-w-md md:max-w-xl mx-auto">
            {homeActions.map((action, idx) => (
              <motion.button
                key={idx}
                className="w-full md:w-[480px] bg-white/90 text-black font-bold py-4 px-4 rounded-xl shadow-xl hover:shadow-2xl hover:bg-white transition flex flex-col items-center relative group"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: idx * 0.08,
                  type: "spring",
                  stiffness: 170,
                  damping: 18,
                }}
                whileHover={{
                  scale: 1.04,
                  boxShadow: "0 4px 40px 4px #6ee7b7",
                  transition: { type: "spring", stiffness: 250 },
                }}
                onClick={() => {
                  if (idx === 0 || idx === 1) {
                    navigate(`/check-registration?embed=true&embed_id=${encodeURIComponent(embedId)}`);
                  } else if (idx === 2) {
                    navigate(`/polling-location?embed=true&embed_id=${encodeURIComponent(embedId)}`);
                  } else if (idx === 3) {
                    navigate(`/vbm?embed=true&embed_id=${encodeURIComponent(embedId)}`);
                  } else if (idx === 4) {
                    navigate(`/reminder-subscription?embed=true&embed_id=${encodeURIComponent(embedId)}`);
                  }
                  // Add more navigation logic for other buttons as you build them!
                }}
              >
                <span className="text-lg md:text-xl flex items-center">
                  {action.title}
                  {/* Optional animated arrow */}
                  <svg
                    className="ml-2 group-hover:translate-x-2 transition-transform duration-200"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </span>
                <span className="text-sm font-normal">{action.subtitle}</span>
              </motion.button>
            ))}
          </div>
          {/* Spacer before footer */}
          <div className="h-24" />
        </div>
      </main>
      {status?.data?.plan === "free" && (<div className="fixed bottom-4 z-[9999999]  right-4">
        <img src={BGLogo} alt="Background Logo" width={50} height={50} />
      </div>)}
    </div>
  );
}
